import { useState, useCallback, useRef, useEffect } from 'react';
import alumnosApiService from '../../../../services/http/alumnosApiService';

const PAGE_SIZE = 100;

// Un usuario de delegación con ambas sedes lanza dos búsquedas con delegación
// distinta por sede. El servidor las resuelve juntas para que el orden y la
// paginación sean globales.
const combinarParams = ({ params, params2 }) =>
  params2
    ? { ...params, empresaId: 'CEDETECGROUP', idDelegacioCntec: params2.idDelegacio }
    : params;

const fetchPage = (searchParams, consulta, offset) =>
  alumnosApiService.tablaAlumnos({
    ...combinarParams(searchParams),
    ...consulta,
    limit: PAGE_SIZE,
    offset,
  });

const descargar = (blob, nombre) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Un error de la exportación llega como blob JSON: se extrae el mensaje.
const leerBlob = (blob) =>
  new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(lector.result);
    lector.onerror = reject;
    lector.readAsText(blob);
  });

const mensajeError = async (err) => {
  try {
    const texto = await leerBlob(err.response.data);
    return JSON.parse(texto).error;
  } catch (_e) {
    return 'No se pudo exportar a Excel.';
  }
};

/**
 * Búsqueda de alumnos con orden y filtros por columna en servidor.
 * `consulta` = { sortBy, sortDir, colFilters } del estado de la tabla; al cambiar
 * se vuelve a pedir la primera página de la última búsqueda.
 */
const useSearchAlumnos = (consulta = {}) => {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const offsetRef = useRef(0);
  const lastParamsRef = useRef(null);
  const consultaRef = useRef(consulta);
  const peticionRef = useRef(0);
  consultaRef.current = consulta;

  const cargarPrimera = useCallback(async () => {
    if (!lastParamsRef.current) return;
    const peticion = peticionRef.current + 1;
    peticionRef.current = peticion;
    setIsFetching(true);
    offsetRef.current = 0;
    try {
      const data = await fetchPage(lastParamsRef.current, consultaRef.current, 0);
      if (peticion !== peticionRef.current) return; // llegó una búsqueda más reciente
      setRows(data.rows);
      setTotal(data.total);
      offsetRef.current = data.rows.length;
    } catch (_err) {
      if (peticion !== peticionRef.current) return;
      setRows([]);
      setTotal(0);
    } finally {
      if (peticion === peticionRef.current) setIsFetching(false);
    }
  }, []);

  const search = useCallback(
    async (searchParams) => {
      if (!searchParams) return;
      lastParamsRef.current = searchParams;
      await cargarPrimera();
    },
    [cargarPrimera],
  );

  // Cambió el orden o un filtro de columna: se repite la última búsqueda.
  const consultaKey = JSON.stringify(consulta);
  const primeraVez = useRef(true);
  useEffect(() => {
    if (primeraVez.current) {
      primeraVez.current = false;
      return;
    }
    cargarPrimera();
  }, [consultaKey, cargarPrimera]);

  const hasMore = rows !== null && rows.length < total;

  const loadMore = useCallback(async () => {
    // Mientras se recarga la primera página (nuevo orden/filtro) no se pide más: el offset es el viejo.
    if (!lastParamsRef.current || isLoadingMore || isFetching || !hasMore) return;
    const peticion = peticionRef.current;
    setIsLoadingMore(true);
    try {
      const data = await fetchPage(lastParamsRef.current, consultaRef.current, offsetRef.current);
      if (peticion !== peticionRef.current) return;
      if (data.rows.length > 0) {
        setRows((prev) => [...(prev || []), ...data.rows]);
        offsetRef.current += data.rows.length;
      }
      setTotal(data.total);
    } catch (_err) {
      // Se conserva lo ya cargado; el centinela volverá a pedir al hacer scroll.
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, isFetching, hasMore]);

  // Valores reales de una columna bajo los filtros actuales (sin el de la propia columna).
  const loadFacets = useCallback(async (columna) => {
    if (!lastParamsRef.current) return [];
    const data = await alumnosApiService.facetasAlumnos({
      ...combinarParams(lastParamsRef.current),
      colFilters: consultaRef.current.colFilters,
      q: consultaRef.current.q,
      columna,
    });
    return data?.valores || [];
  }, []);

  const exportar = useCallback(async (columnas) => {
    if (!lastParamsRef.current) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const blob = await alumnosApiService.exportarAlumnos({
        ...combinarParams(lastParamsRef.current),
        ...consultaRef.current,
        columnas,
      });
      descargar(blob, `alumnos-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      setExportError(await mensajeError(err));
    } finally {
      setIsExporting(false);
    }
  }, []);

  return {
    data: rows ? { rows, total, hasMore } : null,
    isFetching,
    isLoadingMore,
    isExporting,
    exportError,
    hasMore,
    search,
    loadMore,
    loadFacets,
    exportar,
  };
};

export default useSearchAlumnos;
